import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

// Detected once — covers older browsers and jsdom (cast keeps
// typescript-eslint from flagging the check as unnecessary).
const supportsObserver =
	typeof (globalThis as { IntersectionObserver?: unknown }).IntersectionObserver !== 'undefined'

/**
 * Returns a ref and a boolean that flips to true (once) when the element
 * first enters the viewport. Environments without IntersectionObserver
 * report `true` immediately.
 */
export const useInViewOnce = <T extends HTMLElement = HTMLDivElement>(
	rootMargin = '0px 0px -10% 0px'
): [RefObject<T>, boolean] => {
	const ref = useRef<T>(null)
	const [inView, setInView] = useState(!supportsObserver)

	useEffect(() => {
		if (inView) return
		const element = ref.current
		if (!element) return
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some(entry => entry.isIntersecting)) {
					setInView(true)
					observer.disconnect()
				}
			},
			{ rootMargin, threshold: 0.1 }
		)
		observer.observe(element)
		return () => observer.disconnect()
	}, [inView, rootMargin])

	return [ref, inView]
}
